import { AssetFormData } from '../components/forms/CreateAsset/steps'

export type VersionBumpType = 'major' | 'minor' | 'patch' | 'none'

// Fields that count as a "change" for triggering a bump.
// Any diff here + any file change → treat as major.
const TRACKED_FIELDS: (keyof AssetFormData)[] = [
    'documentGroup',
    'documentType',
    'confidentialityLevel',
    'integrityLevel',
    'availabilityLevel',
    'assetName',
    'departmentId',
    'startDate',
    'endDate',
    'assetDescription',
    'isDownloadRestricted',
]

const isEqual = (a: unknown, b: unknown): boolean =>
    JSON.stringify(a) === JSON.stringify(b)

/**
 * Any change (field or file) bumps major. No change → 'none'.
 * Simplified from semver classification — BE treats any file change as major
 * anyway, so we don't distinguish minor/patch on the client.
 */
export function computeVersionBump(
    original: Partial<AssetFormData>,
    current: AssetFormData,
    filesChanged: boolean
): VersionBumpType {
    if (filesChanged) return 'major'
    const hasFieldChange = TRACKED_FIELDS.some(
        (key) => !isEqual(original[key], current[key])
    )
    return hasFieldChange ? 'major' : 'none'
}

/**
 * Compute the next semantic version based on bump type.
 * Major resets minor+patch, Minor resets patch.
 */
export function computeNextVersion(
    current: { major: number; minor: number; patch: number },
    bumpType: VersionBumpType
): { major: number; minor: number; patch: number } {
    switch (bumpType) {
        case 'major':
            return { major: current.major + 1, minor: 0, patch: 0 }
        case 'minor':
            return {
                major: current.major,
                minor: current.minor + 1,
                patch: 0,
            }
        case 'patch':
            return {
                major: current.major,
                minor: current.minor,
                patch: current.patch + 1,
            }
        default:
            return current
    }
}

/**
 * Format version object as display string: "v1.2.3"
 */
export function formatVersion(version: {
    major: number
    minor: number
    patch: number
}): string {
    return `v${version.major}.${version.minor}.${version.patch}`
}
